// import { useState, useCallback, useEffect } from "react";
// import { CalendarEvent } from "../utils/types";
// import * as api from "./calendarApi";

// interface UseCalendarEventsProps {
//   organizationId: string;
//   initialFilters?: {
//     start?: string;
//     end?: string;
//     assigned_to?: string;
//     event_type?: string;
//   };
// }

// export const useCalendarEvents = ({
//   organizationId,
//   initialFilters,
// }: UseCalendarEventsProps) => {
//   const [events, setEvents] = useState<CalendarEvent[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [filters, setFilters] = useState<Record<string, any>>(
//     initialFilters || {}
//   );

//   if (!organizationId) {
//     throw new Error("organizationId is required in useCalendarEvents hook.");
//   }

//   const fetch = useCallback(
//     async (extraParams?: Record<string, any>) => {
//       setLoading(true);
//       setError(null);
//       try {
//         const params = { ...filters, ...extraParams };
//         const res = await api.fetchEvents(organizationId, params);
//         setEvents(res.data);
//       } catch (err: any) {
//         console.error("Error fetching events:", err);
//         setError(
//           err?.response?.data?.detail ||
//             err.message ||
//             "Failed to fetch calendar events."
//         );
//       } finally {
//         setLoading(false);
//       }
//     },
//     [filters, organizationId]
//   );

//   useEffect(() => {
//     fetch();
//   }, [fetch]);

//   const create = useCallback(
//     async (payload: Partial<CalendarEvent>) => {
//       if (!organizationId)
//         throw new Error("Organization ID required to create event.");
//       setLoading(true);
//       try {
//         const res = await api.createEvent(organizationId, payload);
//         setEvents((prev) => [res.data, ...prev]);
//         return res.data;
//       } catch (err: any) {
//         console.error("Error creating event:", err);
//         throw err;
//       } finally {
//         setLoading(false);
//       }
//     },
//     [organizationId]
//   );

//   const update = useCallback(
//     async (id: string, payload: Partial<CalendarEvent>) => {
//       if (!organizationId)
//         throw new Error("Organization ID required to update event.");
//       setLoading(true);
//       try {
//         const res = await api.updateEvent(organizationId, id, payload);
//         setEvents((prev) =>
//           prev.map((ev) => (ev.id === id ? res.data : ev))
//         );
//         return res.data;
//       } catch (err: any) {
//         console.error("Error updating event:", err);
//         throw err;
//       } finally {
//         setLoading(false);
//       }
//     },
//     [organizationId]
//   );

//   const remove = useCallback(
//     async (id: string) => {
//       if (!organizationId)
//         throw new Error("Organization ID required to delete event.");
//       setLoading(true);
//       try {
//         await api.deleteEvent(organizationId, id);
//         setEvents((prev) => prev.filter((e) => e.id !== id));
//       } catch (err: any) {
//         console.error("Error deleting event:", err);
//         throw err;
//       } finally {
//         setLoading(false);
//       }
//     },
//     [organizationId]
//   );

//   const setFilter = useCallback((nextFilters: Record<string, any>) => {
//     setFilters((prev) => ({ ...prev, ...nextFilters }));
//   }, []);

//   return {
//     events,
//     loading,
//     error,
//     filters,
//     setFilter,
//     fetch,
//     create,
//     update,
//     remove,
//   };
// };

// src/hooks/useCalendarEvents.ts
import { useState, useCallback, useEffect } from "react";
import { CalendarEvent } from "../utils/types";
import * as api from "./calendarApi";

interface UseCalendarEventsProps {
  organizationId: string;
  initialFilters?: Record<string, any>;
  autoRefreshMs?: number;
}

const normalizeEvents = (data: unknown): CalendarEvent[] => {
  if (Array.isArray(data)) {
    return data as CalendarEvent[];
  }

  if (data && typeof data === "object" && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: CalendarEvent[] }).results;
  }

  return [];
};

export const useCalendarEvents = ({
  organizationId,
  initialFilters,
  autoRefreshMs = 30000,
}: UseCalendarEventsProps) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(initialFilters || {});

  const fetch = useCallback(
    async (extraParams?: Record<string, any>) => {
      setLoading(true);
      setError(null);
      try {
        const params = { ...filters, ...extraParams };
        const res = await api.fetchEvents(organizationId, params);
        setEvents(normalizeEvents(res.data));
      } catch (err: any) {
        console.error("Error fetching events:", err);
        setError(err?.response?.data?.detail || err.message || "Failed to load events.");
      } finally {
        setLoading(false);
      }
    },
    [filters, organizationId]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!organizationId || autoRefreshMs <= 0) {
      return;
    }

    const interval = window.setInterval(() => {
      fetch();
    }, autoRefreshMs);

    return () => window.clearInterval(interval);
  }, [autoRefreshMs, fetch, organizationId]);

  useEffect(() => {
    const handleNotification = (event: Event) => {
      const customEvent = event as CustomEvent<{ data?: Record<string, string> }>;
      const eventType = customEvent.detail?.data?.type;
      if (eventType === 'calendar_event') {
        fetch();
      }
    };

    window.addEventListener('pipeline:notification', handleNotification as EventListener);
    return () => {
      window.removeEventListener('pipeline:notification', handleNotification as EventListener);
    };
  }, [fetch]);

  const create = useCallback(async (payload: Partial<CalendarEvent>) => {
    setLoading(true);
    try {
      const res = await api.createEvent(organizationId!, payload);
      setEvents((prev) => [res.data, ...prev]);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const update = useCallback(async (id: string, payload: Partial<CalendarEvent>) => {
    setLoading(true);
    try {
      const res = await api.updateEvent(organizationId!, id, payload);
      setEvents((prev) => prev.map((ev) => (ev.id === id ? res.data : ev)));
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const remove = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await api.deleteEvent(organizationId!, id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const setFilter = useCallback((nextFilters: Record<string, any>) => {
    setFilters((prev) => ({ ...prev, ...nextFilters }));
  }, []);

  return { events, loading, error, filters, setFilter, fetch, create, update, remove };
};
